import React, { useEffect, useState, useMemo } from "react";
import { ArrowLeft, Search, ExternalLink, MapPin, Users, CheckCircle, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { partnerLiftPartners } from "../../data/partnerData";

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const PartnerLiftPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return partnerLiftPartners;
    const q = search.toLowerCase();
    return partnerLiftPartners.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.ort.toLowerCase().includes(q) ||
        p.plz.includes(q)
    );
  }, [search]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative bg-[#1d1d1f] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #4a7c59 0%, transparent 50%), radial-gradient(circle at 75% 50%, #4a7c59 0%, transparent 50%)' }} />
        </div>
        <div className="max-w-[1200px] mx-auto px-6 relative z-10">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-white/50 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
          <div className="flex items-center gap-5 mb-6">
            <img
              src="/uploads/partnerlift.png"
              alt="PartnerLIFT"
              className="h-14 object-contain"
            />
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-4"
          >
            PartnerLIFT-Kooperation.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/60 text-lg max-w-2xl mb-10"
          >
            Wir sind Mitglied bei PartnerLIFT, einer Vereinigung selbständiger, mittelständischer
            Arbeitsbühnenvermieter mit Standorten überall in Deutschland und einigen europäischen Ländern.
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap gap-8"
          >
            <div>
              <p className="text-3xl font-bold text-white">{partnerLiftPartners.length}+</p>
              <p className="text-xs text-white/40 uppercase tracking-wider">Partner</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">15.000+</p>
              <p className="text-xs text-white/40 uppercase tracking-wider">Arbeitsbühnen</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">24/7</p>
              <p className="text-xs text-white/40 uppercase tracking-wider">Verfügbarkeit</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Advantages */}
      <section className="py-20 bg-[#f5f5f7]">
        <div className="max-w-[1200px] mx-auto px-6">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-5 text-center">
              Ihre Vorteile im Netzwerk.
            </h2>
            <p className="text-lg text-[#6e6e73] max-w-2xl mx-auto text-center mb-14">
              Das PartnerLIFT-Netzwerk bietet Ihnen zahlreiche Vorteile für Ihren Projekterfolg.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Bundesweite Verfügbarkeit",
                desc: "Egal wo Ihr Projekt ist – über unser PartnerLIFT-Netzwerk stellen wir Geräte überall in Deutschland und in einigen Nachbarländern bereit.",
                icon: MapPin,
              },
              {
                title: "Qualitätsgeprüfte Partner",
                desc: "Alle PartnerLIFT-Betriebe verpflichten sich zu regelmäßigen Qualitätsprüfungen – von Wartung über Brandschutz bis zur Auftragsbearbeitung.",
                icon: CheckCircle,
              },
              {
                title: "Lokale Bedingungen überall",
                desc: "Profitieren Sie von den gleichen erstklassigen Konditionen und Serviceleistungen wie bei Ihrem Vermieter vor der Haustür.",
                icon: Users,
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="p-8 rounded-3xl bg-white border border-[#e5e5e5] hover:shadow-xl hover:shadow-black/5 transition-all duration-500 h-full">
                  <div className="w-12 h-12 rounded-2xl bg-[#4a7c59]/10 flex items-center justify-center mb-5">
                    <item.icon size={22} className="text-[#4a7c59]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1d1d1f] tracking-tight mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#6e6e73] leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-5 text-center">
              Interaktive Partnerkarte.
            </h2>
            <p className="text-lg text-[#6e6e73] max-w-2xl mx-auto text-center mb-10">
              Entdecken Sie alle PartnerLIFT-Standorte auf einen Blick.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="rounded-3xl overflow-hidden border border-[#e5e5e5] shadow-xl shadow-black/5">
              <iframe
                src="/partnerkarte.html"
                className="w-full h-[700px] border-0"
                title="PartnerLIFT Karte"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Partner List */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-3 text-center">
              Unsere Partner
            </h2>
            <p className="text-lg text-[#6e6e73] max-w-xl mx-auto text-center mb-10">
              Mit diesen Partnern arbeiten wir zusammen:
            </p>
          </FadeIn>

          {/* Search */}
          <div className="max-w-md mx-auto mb-10">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#aeaeb2]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Partner, Ort oder PLZ suchen..."
                className="w-full bg-[#f5f5f7] border-0 rounded-xl pl-11 pr-4 py-3.5 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
              />
            </div>
            <p className="text-xs text-[#aeaeb2] text-center mt-2">
              {filtered.length} Partner gefunden
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((partner, i) => (
              <a
                key={i}
                href={partner.url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`group flex items-center justify-between px-5 py-4 rounded-2xl transition-all duration-300 ${
                  partner.highlight
                    ? "bg-[#4a7c59]/5 border-2 border-[#4a7c59]/20 hover:border-[#4a7c59]/40"
                    : "bg-[#f5f5f7] hover:bg-white hover:shadow-lg hover:shadow-black/5 border border-transparent hover:border-[#e5e5e5]"
                }`}
              >
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    partner.highlight ? "text-[#4a7c59]" : "text-[#1d1d1f]"
                  }`}>
                    {partner.name}
                  </p>
                  <p className="text-xs text-[#6e6e73] mt-0.5">
                    {partner.plz} {partner.ort}
                  </p>
                </div>
                <ExternalLink
                  size={14}
                  className="text-[#aeaeb2] group-hover:text-[#4a7c59] transition-colors flex-shrink-0 ml-3"
                />
              </a>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#6e6e73]">Kein Partner für "{search}" gefunden.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f5f5f7]">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight mb-3">
              Gerät bundesweit anfragen
            </h2>
            <p className="text-sm text-[#6e6e73] mb-6">
              Egal wo in Deutschland – ein Anruf genügt und wir organisieren das passende Gerät über unser PartnerLIFT-Netzwerk.
            </p>
            <a
              href="tel:+492871242660"
              className="inline-flex items-center gap-2 bg-[#4a7c59] hover:bg-[#3d6a4a] text-white text-sm font-medium px-8 py-3.5 rounded-xl transition-all duration-300 hover:shadow-lg"
            >
              02871 / 242 660
            </a>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default PartnerLiftPage;
